import { Injector, Signal, computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AuthService } from '@auth0/auth0-angular';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  catchError,
  combineLatest,
  filter,
  firstValueFrom,
  map,
  of,
  pipe,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs';
import { CustomerAccessLevelEnum } from '@/app/core/auto-generated/api.service';
import {
  UserInfo,
  accessibleCustomerCount,
  decodePermissions,
  hasGrant,
  toUserInfo,
} from './session.claims';

/**
 * The single source of truth for the signed-in user's session — id-token claims
 * (`isAsm`, `customerId`, …) and access-token `permissions` — projected into
 * signals.
 *
 * Auth0 is RxJS-native, so there is exactly one Observable→state seam: the live
 * `load` {@link rxMethod} that bridges `idTokenClaims$` + the access token into
 * `withState`. Everything downstream reads pure signals (`isAsm`, `can(p)`, …).
 * `userInfo$` is exposed for the few consumers that still do `switchMap`
 * pipework; the async guard methods decode a fresh token on demand (a guard
 * must await token resolution before deciding — a signal starts empty).
 *
 * Loaded once at startup via the post-auth `provideAppInitializer` in
 * `app.config.ts`; the loader stays subscribed so token refresh / logout keep
 * the store in sync. Mirrors the house store style (see `UserSettingsStore`).
 */
type SessionState = {
  userInfo: UserInfo | null;
  permissions: ReadonlySet<string>;
  /** True once the auth streams have emitted at least once. */
  loaded: boolean;
};

const initialState: SessionState = {
  userInfo: null,
  permissions: new Set<string>(),
  loaded: false,
};

export const SessionStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ userInfo }) => ({
    isAsm: computed(() => userInfo()?.IsASM ?? false),
    customerId: computed(() => userInfo()?.CustomerId),
    /** Customers this user may bill a shipment to (id-token `billCustomerIds`). */
    billCustomerIds: computed(() => userInfo()?.BillCustomerIds ?? []),
    email: computed(() => userInfo()?.Email ?? ''),
    fullname: computed(() => userInfo()?.fullname ?? ''),
    profilePicture: computed(() => userInfo()?.profilePicture ?? ''),
    hasAdditionalCreateCustomers: computed(
      () => userInfo()?.HasAdditionalCreateCustomers ?? false
    ),
    userReady: computed(() => userInfo() !== null),
  })),
  withProps((store) => ({
    // Compatibility boundary for consumers that still pipe over the session
    // (customers-store, PostHog identity, and others).
    userInfo$: toObservable(store.userInfo).pipe(
      filter((user): user is UserInfo => user !== null)
    ),
  })),
  withMethods((store) => {
    const auth = inject(AuthService);
    const injector = inject(Injector);

    /** Decode a freshly-resolved access token — for the awaitable guard path. */
    const resolvedPermissions = async (): Promise<ReadonlySet<string>> => {
      try {
        return decodePermissions(
          (await firstValueFrom(auth.getAccessTokenSilently())) ?? null
        );
      } catch {
        return new Set<string>();
      }
    };

    return {
      /**
       * Reactive permission gate. `dev:all` is a universal override, so a
       * developer account satisfies every check. Use for synchronous UI gating.
       */
      can: (permission: string): Signal<boolean> =>
        computed(() => hasGrant(store.permissions(), permission)),
      /** Reactive gate — true if any of `permissions` is granted (honours `dev:all`). */
      canAny: (permissions: string[]): Signal<boolean> =>
        computed(() =>
          permissions.some((p) => hasGrant(store.permissions(), p))
        ),

      /**
       * Whether to offer a customer-scope filter at `accessLevel` (default
       * `View`): ASM users always (they can pick any customer); external users
       * only when the id-token claims grant access to more than one customer —
       * a single-customer user has nothing to choose between, so the filter is
       * hidden. Use to gate the customer filter pill on list screens.
       */
      customerScopeVisible: (
        accessLevel?: CustomerAccessLevelEnum
      ): Signal<boolean> =>
        computed(() => {
          if (store.isAsm()) {
            return true;
          }
          const info = store.userInfo();
          return info ? accessibleCustomerCount(info, accessLevel) > 1 : false;
        }),

      // Awaitable methods for route guards — decode the resolved token so the
      // decision never races the (initially empty) reactive signal.
      async isGranted(permission: string): Promise<boolean> {
        return hasGrant(await resolvedPermissions(), permission);
      },
      async isGrantedAny(permissions: string[]): Promise<boolean> {
        if (permissions.length === 0) {
          return true;
        }
        const granted = await resolvedPermissions();
        return permissions.some((p) => hasGrant(granted, p));
      },

      /**
       * Awaitable ASM check for route guards / nav resolution — waits for the
       * id-token claims to resolve (the `isAsm` signal starts empty, so a guard
       * that reads it directly would race the first emission). Mirrors the
       * awaitable permission methods above.
       */
      async isAsmResolved(): Promise<boolean> {
        if (store.loaded()) {
          return store.userInfo()?.IsASM ?? false;
        }

        return firstValueFrom(
          toObservable(store.loaded, { injector }).pipe(
            filter(Boolean),
            take(1),
            map(() => store.userInfo()?.IsASM ?? false),
            timeout({ first: 10000, with: () => of(false) }),
            catchError(() => of(false))
          )
        );
      },

      /**
       * Start the live projection of the Auth0 token streams into state. Safe
       * to call repeatedly — only the first call subscribes; that subscription
       * then stays live so refresh/logout keep the store current.
       */
      load: rxMethod<void>(
        pipe(
          filter(() => !store.loaded()),
          switchMap(() =>
            combineLatest([
              auth.idTokenClaims$,
              auth.isAuthenticated$.pipe(
                switchMap((authenticated) =>
                  authenticated
                    ? auth.getAccessTokenSilently().pipe(
                        map((token) => ({ authenticated, token })),
                        catchError(() => of({ authenticated, token: null }))
                      )
                    : of({ authenticated, token: null })
                )
              ),
            ])
          ),
          tap(([claims, tokenContext]) =>
            patchState(store, {
              userInfo:
                tokenContext.authenticated && claims
                  ? toUserInfo(claims as unknown as Record<string, unknown>)
                  : null,
              permissions: decodePermissions(tokenContext.token ?? null),
              loaded: true,
            })
          )
        )
      ),
    };
  })
);
